import React from 'react';
import KoobTitle from '@koob/ui/src/components/KoobTitle';
import KoobParagraph from '@koob/ui/src/components/KoobParagraph';
import KoobModal from '@koob/ui/src/components/KoobModal';
import KoobPlaceholder from '@koob/ui/src/components/KoobPlaceholder';
import { isRetryableError, roomsCompositionToFilters } from '@koob/utils';
import { dayjs, toDateString, type DateInput } from '@koob/dates';
import { useHotelsMarketplace } from '../hooks/useHotelQueries';
import { useTripFilters } from '../hooks/useTripStoreSelectors';

interface TripDayHotelSuggestedModalProps {
  open: boolean;
  close: () => void;
  hotelIds: (string | number)[];
  date: DateInput;
  /** Drops the date range from the search and skips the price/CTA on each card. */
  isIncluded?: boolean;
  /** Overrides the config locale, same as `useHotelsMarketplace`. */
  locale?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  emptyLabel: React.ReactNode;
  /** The app owns the hotel card — it renders its own price slot and labels on it. */
  renderHotelCard: (hotel: any) => React.ReactNode;
}

export default function TripDayHotelSuggestedModal({
  open,
  close,
  hotelIds,
  date,
  isIncluded = false,
  locale,
  title,
  description,
  emptyLabel,
  renderHotelCard,
}: TripDayHotelSuggestedModalProps) {
  const tripFilters = useTripFilters();

  const filters = {
    dmcId: tripFilters?.dmcId?.value
      ? Number(tripFilters.dmcId.value)
      : undefined,
    cities: [],
    countries: [],
    kinds: [],
    regions: [],
    styles: [],
    sustainability: null,
    startAt: !isIncluded ? toDateString(date) : undefined,
    endAt: !isIncluded ? toDateString(dayjs(date).add(1, 'day')) : undefined,
    hotels: hotelIds,
    rooms: roomsCompositionToFilters(tripFilters?.composition),
    tripId: tripFilters?.tripId,
  };

  const { data, isLoading } = useHotelsMarketplace(filters, {
    locale,
    enabled: !!hotelIds?.length && !!open,
    // A 4xx fails identically on every replay — retrying it just spins.
    retry: (_count: number, error: unknown) => isRetryableError(error),
    retryDelay: () => 5000,
  });

  const hotels = (data as any)?.data?.data ?? [];
  const loading = isLoading || (data as any)?.data?.state === 'inProgress';

  return (
    <KoobModal
      open={open}
      setOpen={close}
      widthClass="max-w-7xl"
      autoFocus={false}
    >
      <div className="text-center">
        <KoobTitle>{title}</KoobTitle>

        {description && (
          <KoobParagraph childClass="">{description}</KoobParagraph>
        )}

        <div className="mt-5 grid grid-cols-3 gap-5">
          {!loading && hotels.length === 0 && (
            <div className="col-span-3 text-center text-gray-500">
              {emptyLabel}
            </div>
          )}

          {hotels.map((hotel: any) => renderHotelCard(hotel))}

          {loading && (
            <>
              <KoobPlaceholder className="w-full h-96 rounded-xl" />
              <KoobPlaceholder className="w-full h-96 rounded-xl" />
              <KoobPlaceholder className="w-full h-96 rounded-xl" />
            </>
          )}
        </div>
      </div>
    </KoobModal>
  );
}
